import { FC, useCallback, useEffect, useMemo } from "react";
import { FormikProps } from "formik";
import { omit } from "lodash-es";
import { useDebounce } from "use-debounce";
import { storeConfig } from "src/config/app-config";
import { useShippingMethodsByLocation } from "src/hooks/useShippingMethodsByLocation";
import { useUpdateCartShippingMethod } from "src/hooks/useUpdateCartShippingMethod";
import { useUpdateCartCustomField } from "src/hooks/useUpdateCartCustomField";
import { useShippingMethodDetails } from "src/hooks/useShippingMethodDetails";
import { ShippingMethodsSkeleton } from "src/molecules/ShippingMethodsSkeleton";
import { getCTCustomField } from "src/utils/ct/attributeHelper";
import {
  getShippingMethodPrice,
  getShippingRate,
} from "src/utils/ct/shippingMethodPrice";
import {
  ShippingMethod as ShippingMethodType,
  Cart,
  Money,
} from "src/types/ctgraphql.d";
import { AddressFilter } from "src/types/graphql.d";
import { Iso2LetterLocale } from "src/lib/Intl";
import { getCartInfo } from "src/utils/ct/cart-helper";
import { Heading } from "@otrium/atoms";
import { Trans } from "@lingui/macro";
import {
  ShippingMethodsContainer,
  ShippingMethodsList,
} from "./ShippingMethods.styled";
import { ShippingMethod } from "./ShippingMethod";
import type { Checkout } from "src/types/forms";

const prepareJsonStringToCtCustomField = (jsonString: string): string =>
  // prettier-ignore
  `\"${jsonString.replaceAll('\"', '\\\"')}\"`;

export type TrackingShippingMethods = Array<{
  name: string;
  price?: Money;
}>;

const ShippingMethods: FC<{
  formik: FormikProps<Checkout>;
  cart: Cart | null;
  setLoadingShipingMethods: (loadingShipingMethods: boolean) => void;
  setTrackingShippingMethods: (
    mappedShippingMethods: TrackingShippingMethods
  ) => void;
  trackingShippingMethods?: TrackingShippingMethods;
}> = ({
  formik,
  cart,
  setLoadingShipingMethods,
  setTrackingShippingMethods,
  trackingShippingMethods,
}) => {
  const { data: shippingMethodsData, loading: loadingShipingMethods } =
    useShippingMethodsByLocation({
      country: cart?.shippingAddress?.country as Iso2LetterLocale,
    });
  const shippingMethods = shippingMethodsData?.shippingMethodsByLocation;

  const [debouncedShippingAddress] = useDebounce(
    formik.values.shippingAddress,
    1000
  );

  const cartInfo = useMemo(() => getCartInfo(cart), [cart]);

  const { data, loading: loadingShipingMethodDetails } =
    useShippingMethodDetails({
      filter: omit(debouncedShippingAddress, [
        "isStreetNumberNotRequired",
      ]) as AddressFilter,
      country: cart?.shippingAddress?.country as Iso2LetterLocale,
    });
  const shippingMethodDetails = data?.shippingMethodDetails;

  const { updateCartShippingMethod, loading: loadingUpdateCartShippingMethod } =
    useUpdateCartShippingMethod();

  const { updateCartCustomField, loading: loadingUpdateCartCustomField } =
    useUpdateCartCustomField();

  const pudoCustomField =
    (getCTCustomField("pudo", cart?.custom?.customFieldsRaw) as string) || "";

  const shippingMethodsFiltered = useMemo(
    () =>
      shippingMethods?.filter((shippingMethod: ShippingMethodType) => {
        const isPudo = shippingMethod.key === "pudo-shipping";
        const isPudoAvailable = !!shippingMethodDetails?.pudos?.length;
        return !isPudo || (isPudo && isPudoAvailable);
      }),
    [shippingMethodDetails, shippingMethods]
  );

  useEffect(() => {
    setLoadingShipingMethods(
      loadingUpdateCartShippingMethod ||
        loadingUpdateCartCustomField ||
        loadingShipingMethods
    );
  }, [
    loadingUpdateCartShippingMethod,
    loadingUpdateCartCustomField,
    loadingShipingMethods,
    setLoadingShipingMethods,
  ]);

  useEffect(() => {
    const pudoValues = (shippingMethodDetails?.pudos || []).map(
      ({ value }) => value
    );
    const isPudoCustomFieldNotAvailable =
      shippingMethodDetails &&
      pudoCustomField &&
      pudoValues &&
      !pudoValues.includes(pudoCustomField);

    if (isPudoCustomFieldNotAvailable) {
      void updateCartCustomField("pudo", `""`, cartInfo);
    }
  }, [cartInfo, shippingMethodDetails, pudoCustomField, updateCartCustomField]);

  useEffect(() => {
    if (!shippingMethodsFiltered?.length || !shippingMethodDetails) {
      return;
    }

    const isAvailableShippingMethod = !!shippingMethodsFiltered.find(
      ({ id }) => id === cart?.shippingInfo?.shippingMethod?.id
    );
    const isPudo = cart?.shippingInfo?.shippingMethod?.key === "pudo-shipping";
    const isPudoAvailable = !!shippingMethodDetails?.pudos?.length;

    if (!isAvailableShippingMethod || (isPudo && !isPudoAvailable)) {
      void updateCartShippingMethod(shippingMethodsFiltered[0].id, cartInfo);
    }
  }, [
    cartInfo,
    cart?.shippingInfo?.shippingMethod?.id,
    cart?.shippingInfo?.shippingMethod?.key,
    shippingMethodsFiltered,
    shippingMethodDetails,
    updateCartShippingMethod,
  ]);

  useEffect(() => {
    if (
      shippingMethodsFiltered?.length &&
      cart &&
      !trackingShippingMethods?.length
    ) {
      const mappedShippingMethods = shippingMethodsFiltered.map(
        (shippingMethod) => {
          return {
            name: shippingMethod.name,
            price: getShippingRate(shippingMethod, storeConfig, cart)?.price,
          };
        }
      );
      setTrackingShippingMethods(mappedShippingMethods);
    }
  }, [
    cart,
    setTrackingShippingMethods,
    shippingMethodsFiltered,
    trackingShippingMethods?.length,
  ]);

  useEffect(() => {
    const isPudo = cart?.shippingInfo?.shippingMethod?.key === "pudo-shipping";
    if (pudoCustomField && !isPudo) {
      void updateCartCustomField("pudo", `""`, cartInfo);
    }
  }, [
    cartInfo,
    cart?.shippingInfo?.shippingMethod?.key,
    pudoCustomField,
    updateCartCustomField,
  ]);

  const onPudoChange = useCallback(
    (value: string) => {
      void updateCartCustomField(
        "pudo",
        prepareJsonStringToCtCustomField(value),
        cartInfo
      );
    },
    [updateCartCustomField, cartInfo]
  );

  return (
    <ShippingMethodsContainer>
      <Heading as="h3" variant="h3">
        <Trans>Delivery option</Trans>
      </Heading>

      {!shippingMethodsFiltered || !cart || loadingShipingMethodDetails ? (
        <ShippingMethodsSkeleton itemHeight={58} />
      ) : (
        <ShippingMethodsList>
          {shippingMethodsFiltered.map((shippingMethod: ShippingMethodType) => (
            <ShippingMethod
              data-testid="checkout-info-form-delivery-option"
              key={shippingMethod.id}
              selected={
                shippingMethod.id === cart.shippingInfo?.shippingMethod?.id
              }
              keyValue={shippingMethod.key}
              name={shippingMethod.localizedName || shippingMethod.name}
              description={shippingMethod.localizedDescription}
              price={getShippingMethodPrice(cart, shippingMethod, storeConfig)}
              pudos={shippingMethodDetails?.pudos}
              pudoValue={pudoCustomField}
              pudoDisabled={loadingUpdateCartCustomField}
              onChangePudo={onPudoChange}
              disabled={
                loadingUpdateCartShippingMethod || loadingUpdateCartCustomField
              }
              onChange={() => {
                void updateCartShippingMethod(shippingMethod.id, cartInfo);
              }}
            />
          ))}
        </ShippingMethodsList>
      )}
    </ShippingMethodsContainer>
  );
};

export { ShippingMethods };
